import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, List, ListItemButton, ListItemText, ListSubheader, TextField, Typography } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { TechniqueKey } from "./types";
import { TECHNIQUES, TechniqueDef } from "./toolRegistry";
import "./ExperientialPanel.css";

interface ExperientialPanelProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
}

const GROUPS: TechniqueDef["group"][] = ["voice", "image", "combined"];

// Accent-insensitive fold so "semá" finds "Sématerápiás szék"
function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function ExperientialPanel(props: ExperientialPanelProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<TechniqueKey | null>(null);
  const [query, setQuery] = useState("");

  const active = TECHNIQUES.find((tq) => tq.key === selected);

  if (active) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: 1, borderColor: "divider", px: 1, py: 0.5, flexShrink: 0 }}>
          <IconButton size="small" onClick={() => setSelected(null)} aria-label={t("experiential.backToLibrary")}>
            <ArrowLeft size={18} />
          </IconButton>
          <Typography variant="subtitle2">{t(`experiential.techniques.${active.key}`)}</Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>{active.render(props)}</Box>
      </Box>
    );
  }

  const matches = (tq: TechniqueDef) =>
    fold(`${t(`experiential.techniques.${tq.key}`)} ${t(`experiential.techniqueHints.${tq.key}`)}`)
      .includes(fold(query));

  const visible = TECHNIQUES.filter(matches);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 1.5, pb: 1, flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("experiential.searchTechniques")}
          inputProps={{ "aria-label": t("experiential.searchTechniques") }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <List dense disablePadding>
          {GROUPS.map((group) => {
            const inGroup = visible.filter((tq) => tq.group === group);
            if (inGroup.length === 0) return null;
            return (
              <React.Fragment key={group}>
                <ListSubheader disableSticky>{t(`experiential.groups.${group}`)}</ListSubheader>
                {inGroup.map((tq) => (
                  <ListItemButton key={tq.key} onClick={() => setSelected(tq.key)}>
                    <ListItemText
                      primary={t(`experiential.techniques.${tq.key}`)}
                      secondary={t(`experiential.techniqueHints.${tq.key}`)}
                    />
                  </ListItemButton>
                ))}
              </React.Fragment>
            );
          })}
          {visible.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: "center" }}>
              {t("experiential.noTechniqueMatch")}
            </Typography>
          )}
        </List>
      </Box>
    </Box>
  );
}
